import { Suspense, lazy, useMemo, useState } from 'react'

import { useQueries, useQuery } from '@tanstack/react-query'
import { isWeekend } from 'date-fns'
import { FileDown, RotateCcw } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import {
  ViewSection,
  ViewSectionContent,
  ViewSectionHeader,
  ViewSectionTitle,
} from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { LoadingDropdownMenuItem } from '@fortem-monorepo/react-ui'
import { LoadingScreen } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuTrigger,
} from '@fortem-monorepo/react-ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { ICGuidelines } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { FUNDS } from '@/constants/links'
import ICGuidelinesGoldExposureTable from '@/pages/funds/ic-guidelines/components/ICGuidelinesGoldExposureTable'
import {
  useDownloadICGuidelinesExcelMutation,
  useForwardICGuidelinesAsEmailMutaiton,
} from '@/react-query/mutations/ic-guidelines'

const ICGuidelinesBetaTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesBetaTable'),
)
const ICGuidelinesBreakdownTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesBreakdownTable'),
)
const ICGuidelinesCostOverNavTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesCostOverNavTable'),
)
const ICGuidelinesCrossHoldingsTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesCrossHoldingsTable'),
)
const ICGuidelinesCurrencyExposuresTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesCurrencyExposuresTable'),
)
const ICGuidelinesFactorWeightsTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesFactorWeightsTable'),
)
const ICGuidelinesHeadlineTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesHeadlineTable'),
)
const ICGuidelinesMTMTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesMTMTable'),
)
const ICGuidelinesStrategicTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesStrategicTable'),
)
const ICGuidelinesTopLevelTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesTopLevelTable'),
)
const ICGuidelinesUCITSTable = lazy(
  () => import('@/pages/funds/ic-guidelines/components/ICGuidelinesUCITSTable'),
)

export interface IcGuidelinesPageProps {}

export const ICGuidelinesPage: React.FC<IcGuidelinesPageProps> = ({}) => {
  const api = useApi()
  const [selectedDate, setSelectedDate] = useState<Date>(getTMinus1())
  const [selectedFund, setSelectedFund] = useState<
    ICGuidelines.GetICGuidelinesTemplateData | undefined
  >(undefined)

  const formattedDate = toISO8601(selectedDate)

  const funds = useQuery({
    queryKey: ['ic-guidelines/funds'],
    queryFn: () => api.icGuidelines.getTemplate(),
  })

  const downloadExcelMutation = useDownloadICGuidelinesExcelMutation()
  const forwardEmailMutation = useForwardICGuidelinesAsEmailMutaiton()

  // ... Only funds that are currently active
  const filteredFunds = useMemo(() => {
    return (funds.data ?? []).filter((fund) => {
      return fund.fund_name && fund.active
    })
  }, [funds])

  const fundOptions = useMemo(() => {
    return filteredFunds.map((fund) => ({
      label: fund.fund_name,
      value: fund.fund_id?.toString(),
    }))
  }, [filteredFunds])

  const params: ICGuidelines.GetICGuidelinesParams = {
    fund_name: selectedFund?.fund_name!,
    value_date: formattedDate,
  }

  const enabled = {
    summary:
      !!selectedFund &&
      Object.values(selectedFund.ic_guidelines_template.summary).some((val) => val),
    currencyExposures:
      !!selectedFund && selectedFund.ic_guidelines_template.summary.currency_exposures,
    mtm: !!selectedFund && selectedFund.ic_guidelines_template.summary.mtm,
    beta:
      !!selectedFund && Object.values(selectedFund.ic_guidelines_template.beta).some((val) => val),
    income:
      !!selectedFund &&
      Object.values(selectedFund.ic_guidelines_template.income).some((val) => val),
    diversifiers:
      !!selectedFund &&
      Object.values(selectedFund.ic_guidelines_template.diversifiers).some((val) => val),
    protection:
      !!selectedFund &&
      Object.values(selectedFund.ic_guidelines_template.protection).some((val) => val),
  }

  // ... All queries that are dependent on fund_name and value_date
  const dependentQueries = useQueries({
    queries: [
      {
        queryKey: ['ic-guidelines/summary', params],
        queryFn: () => api.icGuidelines.getSummary(params),
        enabled: enabled.summary,
      },
      {
        queryKey: ['ic-guidelines/currency-exposures', params],
        queryFn: () => api.icGuidelines.getCurrencyExposures(params),
        enabled: enabled.currencyExposures,
      },
      {
        queryKey: ['ic-guidelines/mtm', params],
        queryFn: () => api.icGuidelines.getMTM(params),
        enabled: enabled.mtm,
      },
      {
        queryKey: ['ic-guidelines/beta', params],
        queryFn: () => api.icGuidelines.getBeta(params),
        enabled: enabled.beta,
      },
      {
        queryKey: ['ic-guidelines/income', params],
        queryFn: () => api.icGuidelines.getIncome(params),
        enabled: enabled.income,
      },
      {
        queryKey: ['ic-guidelines/diversifiers', params],
        queryFn: () => api.icGuidelines.getVectors(params),
        enabled: enabled.diversifiers,
      },
      {
        queryKey: ['ic-guidelines/protection', params],
        queryFn: () => api.icGuidelines.getProtection(params),
        enabled: enabled.protection,
      },
    ],
  })

  const [summary, currencyExposures, mtm, beta, income, vectors, protection] = dependentQueries

  const hasCompletedQueries = dependentQueries.some((query) => {
    return !query.isFetching
  })

  const canDownload = hasCompletedQueries && selectedFund?.fund_number && selectedDate

  const selectFund = (fundId: number) => {
    const fund = filteredFunds[filteredFunds.map((fund) => fund.fund_id).indexOf(fundId)]
    setSelectedFund(fund)
  }

  const showSection = (
    key: keyof ICGuidelines.GetICGuidelinesTemplateData['ic_guidelines_template'],
  ) => {
    return Object.values(selectedFund?.ic_guidelines_template[key] ?? {}).some((val) => val)
  }

  return (
    <Page metadata={{ title: 'IC Guidelines' }} permissions={FUNDS.IC_GUIDELINES.permissions}>
      <HeaderConfigBar sticky>
        <div className={'flex items-center gap-2'}>
          <DatePickerWithControls
            selected={selectedDate}
            todayButton={{
              enabled: true,
              text: 'T - 1',
              date: getTMinus1(),
            }}
            disabled={(date) => isWeekend(date) || date > getTMinus1()}
            size="sm"
            onSelect={(date) => {
              date && setSelectedDate(date)
            }}
          />
          <SearchSelect
            size={'sm'}
            options={fundOptions}
            placeholder={'Select a fund...'}
            value={selectedFund?.fund_id.toString()}
            setValue={(val) => selectFund(parseInt(val))}
          />
          <LoadingButton
            size="sm"
            variant={'outline'}
            onClick={() => funds.refetch()}
            icon={<RotateCcw />}
          >
            Refresh funds
          </LoadingButton>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrap in div to allow 'asChild' tooltip to work properly with nested button components */}
                <div>
                  <DropdownMenuTrigger asChild>
                    <Button variant={'outline'} size="sm" disabled={!canDownload}>
                      <FileDown />
                      Download
                    </Button>
                  </DropdownMenuTrigger>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side={'right'}
                className={cn('max-w-44 text-center', {
                  ['hidden']: canDownload,
                })}
              >
                You must receive data before you can download.
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align={'start'}>
              <DropdownMenuItemIndicator />
              <LoadingDropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                }}
                onClick={async () => downloadExcelMutation.mutateAsync(params)}
              >
                As Excel
              </LoadingDropdownMenuItem>
              <LoadingDropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                }}
                onClick={async () => forwardEmailMutation.mutateAsync(params)}
              >
                By Email
              </LoadingDropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </HeaderConfigBar>
      <ViewContainer className={'relative h-full'}>
        {funds.isFetching && <LoadingScreen />}
        <Suspense fallback={<LoadingScreen />}>
          <div className={'flex flex-col gap-6'}>
            {showSection('summary') && (
              <ViewSection>
                <ViewSectionHeader>
                  <ViewSectionTitle>Summary</ViewSectionTitle>
                </ViewSectionHeader>
                <ViewSectionContent>
                  <div className={'flex flex-col gap-6'}>
                    <div className={'flex gap-6 w-full'}>
                      {selectedFund?.ic_guidelines_template.summary.headline && (
                        <div>
                          <ICGuidelinesHeadlineTable
                            query={summary}
                            domLayout={'print'}
                            rowData={summary.data?.summary ?? []}
                          />
                        </div>
                      )}
                      {(selectedFund?.ic_guidelines_template.summary.cross_holdings ||
                        selectedFund?.ic_guidelines_template.summary.currency_exposures) && (
                        <div className={'flex flex-col gap-6 flex-2'}>
                          {selectedFund?.ic_guidelines_template.summary.cross_holdings && (
                            <ICGuidelinesCrossHoldingsTable
                              query={summary}
                              rowData={summary.data?.cis}
                            />
                          )}
                          {selectedFund?.ic_guidelines_template.summary.currency_exposures && (
                            <ICGuidelinesCurrencyExposuresTable
                              query={currencyExposures}
                              rowData={currencyExposures.data?.all_labels}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    {selectedFund?.ic_guidelines_template.summary.mtm && (
                      <ICGuidelinesMTMTable query={mtm} rowData={mtm.data} />
                    )}
                  </div>
                </ViewSectionContent>
              </ViewSection>
            )}

            {showSection('income') && (
              <ViewSection>
                <ViewSectionHeader>
                  <ViewSectionTitle>Income</ViewSectionTitle>
                </ViewSectionHeader>
                <ViewSectionContent>
                  <ICGuidelinesUCITSTable query={income} rowData={income.data} />
                </ViewSectionContent>
              </ViewSection>
            )}

            {showSection('beta') && (
              <ViewSection>
                <ViewSectionHeader>
                  <ViewSectionTitle>Beta</ViewSectionTitle>
                </ViewSectionHeader>
                <ViewSectionContent>
                  <ICGuidelinesBetaTable query={beta} rowData={beta.data} />
                </ViewSectionContent>
              </ViewSection>
            )}

            {showSection('diversifiers') && (
              <ViewSection>
                <ViewSectionHeader>
                  <ViewSectionTitle>Diversifiers</ViewSectionTitle>
                </ViewSectionHeader>
                <ViewSectionContent>
                  <div className={'flex flex-col gap-6'}>
                    {selectedFund?.ic_guidelines_template.diversifiers
                      .diversifiers_details_top_level && (
                      <ICGuidelinesStrategicTable
                        query={vectors}
                        rowData={vectors.data?.diversifiers_details_breakdown}
                      />
                    )}
                    {selectedFund?.ic_guidelines_template.diversifiers
                      .diversifiers_details_breakdown && (
                      <ICGuidelinesFactorWeightsTable
                        query={vectors}
                        rowData={vectors.data?.diversifiers_details_top_level}
                      />
                    )}
                    {selectedFund?.ic_guidelines_template.diversifiers.gold_exposure && (
                      <ICGuidelinesGoldExposureTable
                        query={vectors}
                        rowData={vectors.data?.gold_exposure}
                      />
                    )}
                  </div>
                </ViewSectionContent>
              </ViewSection>
            )}

            {showSection('protection') && (
              <ViewSection>
                <ViewSectionHeader>
                  <ViewSectionTitle>Protection</ViewSectionTitle>
                </ViewSectionHeader>
                <ViewSectionContent>
                  <div className={'flex flex-col gap-6'}>
                    {selectedFund?.ic_guidelines_template.protection.top_level && (
                      <ICGuidelinesTopLevelTable
                        query={protection}
                        rowData={protection.data?.protection_details_top_level}
                      />
                    )}
                    {selectedFund?.ic_guidelines_template.protection.breakdown && (
                      <ICGuidelinesBreakdownTable
                        query={protection}
                        rowData={protection.data?.protection_details_breakdown}
                      />
                    )}
                    {selectedFund?.ic_guidelines_template.protection.cost_over_nav && (
                      <ICGuidelinesCostOverNavTable
                        query={protection}
                        rowData={protection.data?.cost_over_nav}
                      />
                    )}
                  </div>
                </ViewSectionContent>
              </ViewSection>
            )}
          </div>
        </Suspense>
      </ViewContainer>
    </Page>
  )
}
